import type { TravelDirection, TravelLegsPage, TravelReadinessAtom } from "@seagames/contracts";
import { type InfiniteData, infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { travelKeys } from "./travel";

/**
 * The logistics desk (Sector 3, user 2026-10-31): ONE day's people on
 * `GET /travel/legs` (`from` = `to` = the day, games' zone) — the same scoped,
 * keyset read A&D's journeys use, so the desk adds no endpoint. Each row
 * already carries the card status, the live transfer and the open bed.
 *
 * The four tabs are the same read with one filter each; their counts are
 * that read's first-page `total` at `limit=1` (one scalar `count(*)` in the
 * API's statement), so a tab's number IS its list's length.
 *
 * Under `travelKeys.all`: every write along the chain (a travel save, a trip,
 * a bed) refreshes the desk with the rest (`invalidateLogistics`).
 */

/** A desk tab: everyone, or the people a step of the chain is still missing for. */
export const DESK_VIEWS = ["all", "no_transfer", "no_room", "not_approved"] as const;
export type DeskView = (typeof DESK_VIEWS)[number];

export interface DeskFilter {
	games_id: string;
	direction: TravelDirection;
	date: string;
	responsible_organization_id?: string | undefined;
	q?: string | undefined;
}

/** A tab → the read's own filter: `no_transfer` (no live transfer, asked for or not), or a readiness atom that fails. */
const viewQuery = (view: DeskView): { no_transfer?: "true"; readiness?: TravelReadinessAtom } =>
	view === "no_transfer" ? { no_transfer: "true" } : view === "no_room" ? { readiness: "bed" } : view === "not_approved" ? { readiness: "card" } : {};

const wire = (f: DeskFilter, view: DeskView): Record<string, string> => {
	const out: Record<string, string> = { games_id: f.games_id, direction: f.direction, from: f.date, to: f.date, ...viewQuery(view) };
	if (f.responsible_organization_id) out.responsible_organization_id = f.responsible_organization_id;
	if (f.q?.trim()) out.q = f.q.trim();
	return out;
};

export const deskKeys = {
	list: (f: DeskFilter, view: DeskView) => [...travelKeys.all, "desk", "list", wire(f, view)] as const,
	count: (f: DeskFilter, view: DeskView) => [...travelKeys.all, "desk", "count", wire(f, view)] as const,
};

/** The day's people on one tab, 50 at a time ("Show more"). */
export const deskLegsOptions = (f: DeskFilter, view: DeskView) =>
	infiniteQueryOptions<TravelLegsPage, Error, InfiniteData<TravelLegsPage>, ReturnType<typeof deskKeys.list>, string | undefined>({
		queryKey: deskKeys.list(f, view),
		queryFn: ({ signal, pageParam }) => api.get<TravelLegsPage>("/travel/legs", { query: { ...wire(f, view), limit: 50, ...(pageParam ? { cursor: pageParam } : {}) }, signal }),
		initialPageParam: undefined,
		getNextPageParam: (last) => last.next_cursor ?? undefined,
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** A tab's count: the same read's first-page total. */
export const deskCountOptions = (f: DeskFilter, view: DeskView) =>
	queryOptions({
		queryKey: deskKeys.count(f, view),
		queryFn: async ({ signal }) => (await api.get<TravelLegsPage>("/travel/legs", { query: { ...wire(f, view), limit: 1 }, signal })).total ?? 0,
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});
